import React, { useState, useEffect, useRef } from "react"
import { NotRegisteredToVoteProps, RegistrationType } from "QuorumGrassroots/pages/campaign/voter-information/types"
import { useCreateRegistrationAction } from "QuorumGrassroots/pages/campaign/voter-information/hooks/useCreateRegistrationAction"
import {
    Container,
    SvgWrapper,
    ActionCallTextWrapper,
    StatusText,
    ActionCallText,
    ExclamationIconWrapper,
    ActionText,
    ExclamationStyledIcon,
    AddressWrapper,
    AddressIconWrapper,
    AddressText,
    AddressStyledIcon,
    RegistrationOptions,
    RegistrationTextWrapper,
    RegistrationText,
    RegistrationLinks,
    OnlineRegistration,
    OnlineRegistrationContent,
    OnlineRegistrationAnchor,
    PaperRegistration,
    PaperRegistrationButton,
    PaperRegistrationContent,
    PaperRegistrationArrow,
    StyledDropdown,
    RegistrationStatusLink,
    RegistrationStatusLinkText,
    StyledAnchor,
    StatusLinkAnchor,
    StyledLinkAnchor,
    PaperRegistrationDropdown,
    PaperRegistrationDropdownOption,
    OptionFlag,
    OptionText,
    OptionAnchor,
    OptionStyledAnchor,
    FallbackText,
} from "./NotRegisteredToVote.styles"
import { organizationPrimaryColor } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/ContainerWithColor/ContainerWithColor"

export const NotRegisteredToVote: React.FC<NotRegisteredToVoteProps> = ({ voterInfo, registrationActionConfig }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const { createRegistrationAction } = useCreateRegistrationAction(registrationActionConfig)

    // Determine which paper registration options are available
    const hasBothPaperForms = Boolean(voterInfo.paper_form_url_en && voterInfo.paper_form_url_es)
    const hasOnlyEnglishForm = Boolean(voterInfo.paper_form_url_en && !voterInfo.paper_form_url_es)
    const hasOnlySpanishForm = Boolean(!voterInfo.paper_form_url_en && voterInfo.paper_form_url_es)
    const hasAnyPaperForm = hasBothPaperForms || hasOnlyEnglishForm || hasOnlySpanishForm
    const hasAnyRegistrationOption = Boolean(voterInfo.online_registration_url) || hasAnyPaperForm

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            toggleDropdown()
        } else if (event.key === "Escape" && isDropdownOpen) {
            setIsDropdownOpen(false)
        }
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside)
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [isDropdownOpen])

    return (
        <Container>
            <SvgWrapper>
                <svg width="292" height="160" viewBox="0 0 292 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M102.903 49.8386L75.1387 89.2852L80.7315 94.3333H191.543L216.026 50.4419L102.903 49.8386Z"
                        fill="url(#paint0_linear_1230_22889)"
                    />
                    <path
                        d="M75.1387 89.2852L74.522 88.8511L74.1375 89.3974L74.6334 89.845L75.1387 89.2852ZM102.903 49.8386L102.907 49.0845L102.513 49.0824L102.287 49.4046L102.903 49.8386ZM216.026 50.4419L216.684 50.8093L216.03 49.6878L216.026 50.4419ZM191.543 94.3333V95.0875H191.986L192.202 94.7007L191.543 94.3333ZM80.7315 94.3333L80.2262 94.8932L80.4415 95.0875H80.7315V94.3333ZM75.1387 89.2852L75.7554 89.7192L103.52 50.2727L102.903 49.8386L102.287 49.4046L74.522 88.8511L75.1387 89.2852ZM102.903 49.8386L102.899 50.5928L216.022 51.1961L216.026 50.4419L216.03 49.6878L102.907 49.0845L102.903 49.8386ZM216.026 50.4419L215.367 50.0746L190.885 93.966L191.543 94.3333L192.202 94.7007L216.684 50.8093L216.026 50.4419ZM191.543 94.3333V93.5792H80.7315V94.3333V95.0875H191.543V94.3333ZM80.7315 94.3333L81.2368 93.7735L75.644 88.7253L75.1387 89.2852L74.6334 89.845L80.2262 94.8932L80.7315 94.3333Z"
                        fill="#808080"
                    />
                    <path d="M172.78 73.2847H116L118.768 67.2847H175L172.78 73.2847Z" fill="#808080" />
                    <path
                        d="M175.885 25.7059L170.003 71.8598H124L130.637 20.1252C145.096 23.0373 159.752 24.8786 174.482 25.634L175.885 25.7059Z"
                        fill="white"
                        stroke="#808080"
                        strokeWidth="1.5083"
                    />
                    <path
                        d="M173.623 25.8567L166.082 72.0105H169.852L176.187 26.3092L173.623 25.8567Z"
                        fill="#808080"
                    />
                    <path
                        d="M75 157.079V115.299V89.356L188.273 89.3561V157.079H75Z"
                        fill="url(#paint1_linear_1230_22889)"
                        stroke="#808080"
                        strokeWidth="1.5083"
                    />
                    <path
                        d="M187.217 89.5069V157.079H216.93L216.567 50.1404L187.217 89.5069Z"
                        fill="url(#paint2_linear_1230_22889)"
                        stroke="#808080"
                        strokeWidth="1.5083"
                        strokeLinejoin="bevel"
                    />
                    <path d="M287.78 157.38H4.22021" stroke="#808080" strokeWidth="1.5083" strokeLinecap="round" />
                    <path
                        d="M141.116 24.1903C134.763 18.0495 135.181 15.4362 130.975 7.93502L143.925 1.59166C144.468 3.48567 144.924 4.80552 147.04 7.37831C149.684 10.5943 156.843 10.0784 162.885 12.7954C167.719 14.969 170.311 20.7152 171.159 23.9508L162.837 23.2017C162.627 22.2196 161.588 20.4305 154.017 20.1974C151.623 20.1237 152.012 22.556 152.656 23.4807C157.503 27.5986 158.993 29.5325 159.131 29.9847C160.774 33.535 158.373 34.1616 156.967 34.0312C154.798 32.8958 152.657 31.318 150.424 29.9463C146.607 27.6016 142.905 25.4513 141.116 24.1903Z"
                        fill="url(#paint3_linear_1230_22889)"
                        stroke="white"
                        strokeWidth="1.5083"
                    />
                    <path
                        d="M121.59 16.3227C124.118 14.4789 127.819 14.7623 129.912 17.0937L149.918 39.3909L171.827 18.0162C173.998 15.8987 177.548 15.6778 179.959 17.4773L180.189 17.6584L180.418 17.8572C182.631 19.8891 182.749 23.1805 180.712 25.3839L180.5 25.6009L157.596 47.947L178.516 71.2624C180.664 73.6562 180.199 77.1529 177.689 79.0787L177.673 79.0964L177.573 79.1693C175.043 81.0142 171.342 80.7262 169.251 78.3964L149.242 56.0974L127.335 77.4749C125.097 79.6591 121.387 79.827 118.975 77.8336C116.466 75.7595 116.335 72.1635 118.662 69.8922L118.662 69.8911L141.565 47.5422L120.647 24.2286C118.466 21.798 118.978 18.2285 121.59 16.3227Z"
                        fill="#E04345"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />
                    <g style={{ mixBlendMode: "overlay" }}>
                        <rect y="0.761719" width="292" height="159" fill={organizationPrimaryColor} />
                    </g>
                    <defs>
                        <linearGradient
                            id="paint0_linear_1230_22889"
                            x1="157.5"
                            y1="6.5"
                            x2="157.5"
                            y2="85"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stopColor="#808080" />
                            <stop offset="1" stopColor="white" />
                        </linearGradient>
                        <linearGradient
                            id="paint1_linear_1230_22889"
                            x1="129"
                            y1="56.6182"
                            x2="129"
                            y2="134.118"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stopColor="#808080" />
                            <stop offset="1" stopColor="white" />
                        </linearGradient>
                        <linearGradient
                            id="paint2_linear_1230_22889"
                            x1="183.5"
                            y1="37.5"
                            x2="219.38"
                            y2="121.963"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stopColor="#808080" />
                            <stop offset="1" stopColor="white" />
                        </linearGradient>
                        <linearGradient
                            id="paint3_linear_1230_22889"
                            x1="163.553"
                            y1="38.7891"
                            x2="141.014"
                            y2="-15.1637"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stopColor="#808080" />
                            <stop offset="1" stopColor="white" />
                        </linearGradient>
                    </defs>
                </svg>
            </SvgWrapper>

            <ActionCallTextWrapper>
                <StatusText>
                    Our records indicate that you are not currently registered to vote in{" "}
                    {voterInfo.state || "your state"}.
                </StatusText>
                <ActionCallText>
                    <ExclamationIconWrapper>
                        <ExclamationStyledIcon icon="exclamation-circle" iconFamily="fas" size="xl" />
                    </ExclamationIconWrapper>
                    <ActionText>Register to vote!</ActionText>
                </ActionCallText>
                {voterInfo.address && (
                    <AddressWrapper>
                        <AddressIconWrapper>
                            <AddressStyledIcon icon="map-marker-alt" iconFamily="far" size="sm" />
                        </AddressIconWrapper>
                        <AddressText>{voterInfo.address}</AddressText>
                    </AddressWrapper>
                )}
            </ActionCallTextWrapper>

            <RegistrationOptions>
                <RegistrationTextWrapper>
                    <RegistrationText>
                        You can register now online or by mail. When you register online, you will be redirected to your
                        state's website.
                    </RegistrationText>
                </RegistrationTextWrapper>
                {!hasAnyRegistrationOption && (
                    <RegistrationTextWrapper>
                        <FallbackText>
                            Registration information is not currently available for your state. Please contact your
                            local election office for assistance.
                        </FallbackText>
                    </RegistrationTextWrapper>
                )}
                <RegistrationLinks>
                    {voterInfo.online_registration_url && (
                        <OnlineRegistration
                            as="a"
                            href={voterInfo.online_registration_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Register to vote online (opens in new window)"
                            onClick={() => createRegistrationAction(RegistrationType.ONLINE)}
                        >
                            <OnlineRegistrationContent>Online registration</OnlineRegistrationContent>
                            <OnlineRegistrationAnchor>
                                <StyledAnchor icon="external-link" iconFamily="far" size="md" aria-hidden="true" />
                            </OnlineRegistrationAnchor>
                        </OnlineRegistration>
                    )}

                    {/* When both English and Spanish forms are available - show dropdown */}
                    {hasBothPaperForms && (
                        <PaperRegistrationButton
                            as="div"
                            ref={dropdownRef}
                            onClick={toggleDropdown}
                            onKeyDown={handleKeyDown}
                            role="button"
                            tabIndex={0}
                            aria-expanded={isDropdownOpen}
                            aria-haspopup="true"
                            aria-label="Paper registration options"
                        >
                            <PaperRegistrationContent>Paper registration</PaperRegistrationContent>
                            <PaperRegistrationArrow>
                                <StyledDropdown
                                    icon={isDropdownOpen ? "angle-up" : "angle-down"}
                                    iconFamily="far"
                                    size="md"
                                    aria-hidden="true"
                                />
                            </PaperRegistrationArrow>
                            {isDropdownOpen && (
                                <PaperRegistrationDropdown>
                                    <PaperRegistrationDropdownOption
                                        as="a"
                                        href={voterInfo.paper_form_url_en}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Download English paper registration form (opens in new window)"
                                        onClick={() => createRegistrationAction(RegistrationType.PAPER)}
                                    >
                                        <OptionFlag>&#x1f1fa;&#x1f1f8;</OptionFlag>
                                        <OptionText>English version</OptionText>
                                        <OptionAnchor>
                                            <OptionStyledAnchor
                                                icon="external-link"
                                                iconFamily="far"
                                                size="sm"
                                                aria-hidden="true"
                                            />
                                        </OptionAnchor>
                                    </PaperRegistrationDropdownOption>
                                    <PaperRegistrationDropdownOption
                                        as="a"
                                        href={voterInfo.paper_form_url_es}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Download Spanish paper registration form (opens in new window)"
                                        onClick={() => createRegistrationAction(RegistrationType.PAPER)}
                                    >
                                        <OptionFlag>&#x1f1ea;&#x1f1f8;</OptionFlag>
                                        <OptionText>Spanish version</OptionText>
                                        <OptionAnchor>
                                            <OptionStyledAnchor
                                                icon="external-link"
                                                iconFamily="far"
                                                size="sm"
                                                aria-hidden="true"
                                            />
                                        </OptionAnchor>
                                    </PaperRegistrationDropdownOption>
                                </PaperRegistrationDropdown>
                            )}
                        </PaperRegistrationButton>
                    )}

                    {/* When only English form is available - show direct link */}
                    {hasOnlyEnglishForm && (
                        <PaperRegistration
                            as="a"
                            href={voterInfo.paper_form_url_en}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download English paper registration form (opens in new window)"
                            onClick={() => createRegistrationAction(RegistrationType.PAPER)}
                        >
                            <PaperRegistrationContent>Paper registration (English version)</PaperRegistrationContent>
                            <PaperRegistrationArrow>
                                <StyledDropdown icon="external-link" iconFamily="far" size="sm" aria-hidden="true" />
                            </PaperRegistrationArrow>
                        </PaperRegistration>
                    )}

                    {/* When only Spanish form is available - show direct link */}
                    {hasOnlySpanishForm && (
                        <PaperRegistration
                            as="a"
                            href={voterInfo.paper_form_url_es}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download Spanish paper registration form (opens in new window)"
                            onClick={() => createRegistrationAction(RegistrationType.PAPER)}
                        >
                            <PaperRegistrationContent>Paper registration (Spanish version)</PaperRegistrationContent>
                            <PaperRegistrationArrow>
                                <StyledDropdown icon="external-link" iconFamily="far" size="sm" aria-hidden="true" />
                            </PaperRegistrationArrow>
                        </PaperRegistration>
                    )}
                </RegistrationLinks>
                {voterInfo.status_url && (
                    <RegistrationStatusLink
                        as="a"
                        href={voterInfo.status_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Check official registration status (opens in new window)"
                    >
                        <RegistrationStatusLinkText>Check official registration status</RegistrationStatusLinkText>
                        <StatusLinkAnchor>
                            <StyledLinkAnchor icon="external-link" iconFamily="far" size="sm" aria-hidden="true" />
                        </StatusLinkAnchor>
                    </RegistrationStatusLink>
                )}
            </RegistrationOptions>
        </Container>
    )
}
